/**
 * Live / průvodce — součást modulu Deploy (tab).
 * Online (beacon/hub) vs offline (USB). Discover → šablony → run + fáze.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, CloudOff, Loader2, Radio, RefreshCw, Rocket, Server } from 'lucide-react';
import { toast } from 'sonner';
import { mcpCall } from '../../lib/mcp';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { NodeAdoptionPanel } from '../../components/NodeAdoptionPanel';

interface DiscoverOut {
  ok: boolean;
  mode: 'online' | 'offline' | 'needs_address';
  deploy_base: string | null;
  source: string;
  message: string;
  templates_from_hub: boolean;
  offline_basic: boolean;
  needs_manual_address?: boolean;
  can_continue_offline?: boolean;
  discovery_order?: string[];
  matched_step?: string;
  phases?: Array<{ id: string; label: string; status?: string }>;
  probed?: Array<{ url: string; ok: boolean; source?: string; error?: string }>;
}

interface TemplatesOut {
  ok: boolean;
  mode: string;
  templates: Array<{
    id: string;
    label: string;
    source?: string;
    description?: string | null;
  }>;
  note?: string;
}

interface RunOut {
  ok: boolean;
  mode?: string;
  run?: {
    id: string;
    phase: string;
    phases: Array<{ id: string; label: string; status: string }>;
    approved: boolean;
    target_hint?: string | null;
  };
}

/** React #31 guard — nikdy nerenderovat plain object jako child. */
function asText(v: unknown, fallback = ''): string {
  if (v == null) return fallback;
  if (
    typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
  ) {
    return String(v);
  }
  try {
    return JSON.stringify(v);
  } catch {
    return fallback;
  }
}

export default function LiveDeployPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [explicitUrl, setExplicitUrl] = useState('');
  const [lanHint, setLanHint] = useState('');
  const [runId, setRunId] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [handoffNote, setHandoffNote] = useState<string | null>(null);

  const discoverQ = useQuery({
    queryKey: ['live-deploy-discover', explicitUrl, lanHint],
    queryFn: () =>
      mcpCall<DiscoverOut>('deploy.live_discover', {
        explicit_url: explicitUrl.trim() || undefined,
        lan_hints: lanHint.trim() ? [lanHint.trim()] : undefined,
      }),
    staleTime: 10_000,
    refetchInterval: 30_000,
  });

  // ?handoff=ldh_… z hubu — redeem na beacon/hub (online) nebo hláška offline
  useEffect(() => {
    const token = searchParams.get('handoff')?.trim();
    if (!token || !token.startsWith('ldh_')) return;
    if (!discoverQ.data) return;

    let cancelled = false;
    (async () => {
      const base = discoverQ.data?.deploy_base?.replace(/\/$/, '');
      if (!base || discoverQ.data.mode !== 'online') {
        if (!cancelled) {
          setHandoffNote(
            t(
              'liveDeploy.handoffNeedOnline',
              'Handoff token je v URL, ale beacon není online — zadej hub a znovu zjisti.',
            ),
          );
        }
        return;
      }
      try {
        const j = await mcpCall<{
          ok?: boolean;
          error?: string;
          handoff_id?: string;
          label?: string;
        }>('deploy.live_handoff_redeem', {
          token,
          deploy_base: base,
          live_id: typeof window !== 'undefined' ? window.location.hostname : undefined,
        });
        if (cancelled) return;
        if (!j.ok) {
          toast.error(
            j.error ??
              t('liveDeploy.handoffRedeemFail', 'Handoff redeem selhal'),
          );
          setHandoffNote(j.error ?? 'redeem fail');
          return;
        }
        toast.success(
          t('liveDeploy.handoffRedeemOk', 'Handoff přijat — {{label}}', {
            label: j.label ?? j.handoff_id ?? 'ok',
          }),
        );
        setHandoffNote(
          t('liveDeploy.handoffRedeemOk', 'Handoff přijat — {{label}}', {
            label: j.label ?? j.handoff_id ?? 'ok',
          }),
        );
        // Drop token from URL (jednorázový)
        const next = new URLSearchParams(searchParams);
        next.delete('handoff');
        setSearchParams(next, { replace: true });
      } catch (e) {
        if (!cancelled) {
          toast.error((e as Error).message);
          setHandoffNote((e as Error).message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [discoverQ.data, searchParams, setSearchParams, t]);

  const mode = discoverQ.data?.mode === 'online'
    ? 'online'
    : discoverQ.data?.mode === 'needs_address'
    ? 'needs_address'
    : 'offline';
  const online = mode === 'online';
  const needsAddress = mode === 'needs_address' ||
    discoverQ.data?.needs_manual_address === true;

  const templatesMode = online ? 'online' : 'offline';
  const templatesQ = useQuery({
    queryKey: ['live-deploy-templates', templatesMode],
    queryFn: () =>
      mcpCall<TemplatesOut>('deploy.live_templates_list', {
        mode: templatesMode,
      }),
    enabled: !!discoverQ.data,
  });

  const runQ = useQuery({
    queryKey: ['live-deploy-run', runId],
    queryFn: () => mcpCall<RunOut>('deploy.live_run_get', { run_id: runId }),
    enabled: !!runId,
    refetchInterval: 5_000,
  });

  const createRun = useMutation({
    mutationFn: () =>
      mcpCall<RunOut>('deploy.live_run_create', {
        mode: online ? 'online' : 'offline',
        deploy_base: discoverQ.data?.deploy_base ?? undefined,
        target_hint: lanHint.trim() || undefined,
        auto_approve: false,
      }),
    onSuccess: (r) => {
      if (!r.ok || !r.run?.id) {
        toast.error(t('liveDeploy.runFail', 'Založení běhu selhalo'));
        return;
      }
      setRunId(r.run.id);
      toast.success(
        online
          ? t(
            'liveDeploy.runOnline',
            'Online běh založen — hub i live sdílí stav',
          )
          : t('liveDeploy.runOffline', 'Offline běh — základní režim bez hubu'),
      );
      qc.invalidateQueries({ queryKey: ['live-deploy-run', r.run.id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const advance = useMutation({
    mutationFn: (body: { action: string; phase_id?: string }) =>
      mcpCall('deploy.live_run_advance', { run_id: runId, ...body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['live-deploy-run', runId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handoff = useMutation({
    mutationFn: () =>
      mcpCall<{
        ok: boolean;
        token?: string;
        handoff_id?: string;
        expires_at?: string;
      }>('deploy.live_handoff_create', {
        label: lanHint.trim() || 'live-target',
      }),
    onSuccess: (r) => {
      if (!r.ok || !r.token) {
        toast.error(t('liveDeploy.handoffFail', 'Handoff selhal'));
        return;
      }
      void navigator.clipboard?.writeText(r.token);
      toast.success(
        t(
          'liveDeploy.handoffOk',
          'Token zkopírován — na live otevři UI s ?handoff=…',
        ),
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const phases = useMemo(() => {
    const fromRun = runQ.data?.run?.phases;
    if (Array.isArray(fromRun) && fromRun.length) return fromRun;
    return discoverQ.data?.phases ?? [];
  }, [runQ.data, discoverQ.data]);

  return (
    <div className='space-y-4 max-w-4xl'>
      <p className='text-sm text-gray-500'>
        {t(
          'liveDeploy.desc',
          'Online = beacon/deploy server (šablony). Offline = USB / bez serveru, základní režim.',
        )}
      </p>

      <NodeAdoptionPanel />

      {/* Režim */}
      <Card>
        <CardHeader className='flex flex-row items-center justify-between gap-2'>
          <CardTitle className='flex items-center gap-2 text-base'>
            {online
              ? <Radio className='text-emerald-400' size={18} />
              : <CloudOff className='text-amber-400' size={18} />}
            {t('liveDeploy.connectivity', 'Konektivita')}
          </CardTitle>
          <Badge
            variant={online ? 'green' : needsAddress ? 'yellow' : 'gray'}
          >
            {online ? 'ONLINE' : needsAddress ? t('livedeploy.zadatAdresu', 'ZADAT ADRESU') : 'OFFLINE'}
          </Badge>
        </CardHeader>
        <CardContent className='space-y-3 text-sm text-gray-300'>
          {discoverQ.isLoading
            ? (
              <div className='flex items-center gap-2 text-gray-500'>
                <Loader2 className='animate-spin' size={14} />
                {t(
                  'liveDeploy.discovering',
                  'Beacon: DHCP parametr → lokální acmbeacon → acmbeacon.<tenant>…',
                )}
              </div>
            )
            : <p>{asText(discoverQ.data?.message)}</p>}
          {discoverQ.data?.deploy_base && (
            <p className='font-mono text-xs text-blue-300'>
              {asText(discoverQ.data.deploy_base)}
              <span className='text-gray-600 ml-2'>
                ({asText(discoverQ.data.source)}
                {discoverQ.data.matched_step ? ` · ${asText(discoverQ.data.matched_step)}` : ''}
                )
              </span>
            </p>
          )}
          {discoverQ.data?.discovery_order && (
            <ol className='text-[11px] text-gray-600 list-decimal pl-4 space-y-0.5'>
              {discoverQ.data.discovery_order.map((s, i) => (
                <li key={`${i}-${asText(s)}`}>{asText(s)}</li>
              ))}
            </ol>
          )}
          <div className='flex flex-wrap gap-2 items-end'>
            <div className='flex-1 min-w-[12rem]'>
              <label className='text-[10px] uppercase text-gray-600'>
                {needsAddress
                  ? t('liveDeploy.hubUrlRequired', 'Adresa hubu / beaconu')
                  : t('liveDeploy.hubUrl', 'Hub / beacon URL (override)')}
              </label>
              <Input
                size='sm'
                className='font-mono'
                value={explicitUrl}
                onChange={(e) => setExplicitUrl(e.target.value)}
                placeholder='https://acmbeacon.example.cz'
              />
            </div>
            <div className='flex-1 min-w-[10rem]'>
              <label className='text-[10px] uppercase text-gray-600'>
                {t('liveDeploy.targetHint', 'Cíl (MAC/IP popisek)')}
              </label>
              <Input
                size='sm'
                className='font-mono'
                value={lanHint}
                onChange={(e) => setLanHint(e.target.value)}
                placeholder='MAXACM00x'
              />
            </div>
            <Button
              variant='secondary'
              size='sm'
              icon={<RefreshCw size={14} />}
              onClick={() => discoverQ.refetch()}
            >
              {t('liveDeploy.rediscover', 'Znovu zjistit')}
            </Button>
          </div>
          {handoffNote && (
            <p className='text-xs text-emerald-400/90 border border-emerald-800/40 rounded-lg px-2 py-1.5'>
              {asText(handoffNote)}
            </p>
          )}
          {needsAddress && (
            <div className='flex flex-wrap gap-2 items-center text-xs text-amber-400/95'>
              <span>
                {t(
                  'liveDeploy.needsAddress',
                  'Beacon se nenašel automaticky. Zadej adresu a znovu zjisti, nebo pokračuj offline.',
                )}
              </span>
              <Button
                size='sm'
                variant='secondary'
                onClick={() => {
                  createRun.mutate();
                }}
                disabled={createRun.isPending}
              >
                {t('liveDeploy.continueOffline', 'Pokračovat offline')}
              </Button>
            </div>
          )}
          {!online && !needsAddress && (
            <p className='text-xs text-amber-500/90'>
              {t(
                'liveDeploy.offlineHint',
                'Offline základní režim — bez šablon z deploy serveru (USB / izolovaná síť).',
              )}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Šablony */}
      <Card>
        <CardHeader>
          <CardTitle className='text-base flex items-center gap-2'>
            <Server size={16} />
            {t('liveDeploy.templates', 'Šablony / edice')}
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-2'>
          {templatesQ.isLoading && <Loader2 className='animate-spin text-gray-600' size={16} />}
          {(templatesQ.data?.templates ?? []).map((tpl) => (
            <button
              type='button'
              key={asText(tpl.id) || JSON.stringify(tpl)}
              onClick={() => setSelectedTemplate(String(tpl.id))}
              className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                selectedTemplate === tpl.id
                  ? 'border-blue-500/50 bg-blue-600/10 text-blue-200'
                  : 'border-gray-800 bg-gray-950/40 text-gray-300 hover:border-gray-600'
              }`}
            >
              <div className='flex items-center gap-2'>
                <span className='font-medium'>{asText(tpl.label)}</span>
                <Badge variant='gray'>{asText(tpl.source) || '?'}</Badge>
              </div>
              {tpl.description != null && asText(tpl.description) !== '' && (
                <div className='text-xs text-gray-500 mt-0.5'>
                  {asText(tpl.description)}
                </div>
              )}
            </button>
          ))}
          {templatesQ.data?.note != null &&
            asText(templatesQ.data.note) !== '' && (
            <p className='text-[11px] text-gray-600'>
              {asText(templatesQ.data.note)}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Běh */}
      <Card>
        <CardHeader className='flex flex-row items-center justify-between'>
          <CardTitle className='text-base flex items-center gap-2'>
            <Rocket size={16} />
            {t('liveDeploy.run', 'Běh průvodce')}
          </CardTitle>
          <div className='flex gap-2'>
            {online && (
              <Button
                size='sm'
                variant='secondary'
                onClick={() => handoff.mutate()}
                disabled={handoff.isPending}
              >
                {t('liveDeploy.mintHandoff', 'Handoff token')}
              </Button>
            )}
            <Button
              size='sm'
              onClick={() => createRun.mutate()}
              disabled={createRun.isPending}
            >
              {createRun.isPending
                ? <Loader2 className='animate-spin' size={14} />
                : t('liveDeploy.startRun', 'Spustit běh')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className='space-y-3'>
          {runId && (
            <p className='text-xs font-mono text-gray-500'>
              run_id: {runId}
              {runQ.data?.run?.phase && (
                <span className='ml-2 text-blue-400'>
                  phase={runQ.data.run.phase}
                </span>
              )}
            </p>
          )}
          <ol className='space-y-1.5'>
            {phases.map((p, i) => {
              const st = asText(p.status) || 'pending';
              const pid = asText(p.id);
              const active = asText(runQ.data?.run?.phase) === pid;
              return (
                <li
                  key={pid || i}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm ${
                    active
                      ? 'bg-blue-600/15 text-blue-200'
                      : st === 'done'
                      ? 'text-emerald-400/90'
                      : 'text-gray-500'
                  }`}
                >
                  {st === 'done'
                    ? <CheckCircle2 size={14} />
                    : (
                      <span className='w-3.5 text-center text-[10px] tabular-nums'>
                        {i + 1}
                      </span>
                    )}
                  <span className='flex-1'>{asText(p.label)}</span>
                  {runId && active && (
                    <Button
                      size='sm'
                      variant='secondary'
                      onClick={() =>
                        advance.mutate({
                          action: 'complete_phase',
                          phase_id: pid,
                        })}
                      disabled={advance.isPending}
                    >
                      {t('liveDeploy.completePhase', 'Hotovo')}
                    </Button>
                  )}
                </li>
              );
            })}
          </ol>
          {runId && runQ.data?.run && !runQ.data.run.approved && (
            <Button
              size='sm'
              onClick={() => advance.mutate({ action: 'approve' })}
              disabled={advance.isPending}
            >
              {t('liveDeploy.approve', 'Odsouhlasit běh (hub / operátor)')}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
